/**
 * Dashboard assessment picker (v3.97.0) — which assessments the Dashboard can
 * show, their Test lists, bands, legend text and Take button.
 *
 * Kemal's decisions, 2026-11-01:
 *   - an "Assessment" dropdown left of "Test Type": Letters & Sounds, High
 *     Frequency Words, CVC Words, Nonsense Word Fluency; the two PA
 *     assessments only where their feature switches are on; a group the
 *     teacher hid in Assessment Settings (HFW, CVC) is left out;
 *   - only Letters & Sounds has a Test list (the shared row table,
 *     assessmentEntry.js); HFW, CVC and NWF band the student's LATEST test,
 *     so the Test Type dropdown is greyed out for them (the PA assessments
 *     list their own tests — each is a separate 5-item test);
 *   - the Take button reads "Take L&S" / "Take HFW" / "Take CVC" /
 *     "Take NWF"; the full name goes in its hover text and screen-reader
 *     label.
 * Not offered: 4K Readiness (no mastery bands exist — bandForScore.js) and
 * PA Diagnostic (bands are tier data per sub-skill; off on prod since
 * v3.84.11). The 4K Readiness note still shows in the notes peek.
 *
 * Bands are the shared bandForScore cuts (School View, Class Report):
 * HFW / CVC 80% / 50% of the test's own word count, NWF 21 / 14 of 25,
 * Letter/Sound as before.
 *
 * Pure module: no Firestore, no React.
 */
import { isFeatureEnabled } from '../constants/featureFlags';
import { TEST_TYPE_LABELS } from '../constants/assessmentTypes';
import { PA4K_TEST_TYPES } from '../constants/pa4kItems';
import { PA_STEP_TYPES } from '../constants/paScreenerItems';
import { isGroupVisible } from './sidebarVisibility';
import { ADMIN_NOT_ADMINISTERED } from './paScreener';
import {
  letterSoundRows,
  letterSoundDocTestTypes,
  runSpecForKey,
  headingForKey,
  takeAssessmentHref,
} from './assessmentEntry';
import {
  HFW_TEST_TYPE, CVC_TEST_TYPE, NWF_TEST_TYPE, LETTER_MAX,
  bandsForTestType, bandRangeText,
} from './bandForScore';

export const LETTER_SOUND_ID = 'letter_sound';
export const ASSESSMENT_PARAM = 'assessment';
export const PA_TEST_PARAM = 'paTest';

/**
 * THE table, in dropdown order. `group` = the sidebar group a teacher can hide
 * (sidebarVisibility.TOGGLEABLE_GROUPS) — or the group whose flag gates it;
 * `flag` = the feature flag it needs; `column` = the result column for an
 * assessment without a Test list; `tests` = the doc test types of one that
 * has its own list (PA); `collection` = where its results live.
 */
const ASSESSMENTS = Object.freeze([
  { id: LETTER_SOUND_ID, label: 'Letters & Sounds', short: 'L&S', collection: 'assessments', resultsHref: '/results', flag: null, group: null },
  { id: 'hfw', label: 'High Frequency Words', short: 'HFW', collection: 'hfw_assessments', column: HFW_TEST_TYPE, href: '/hfw/assessment', resultsHref: '/hfw/results', flag: null, group: 'High Frequency Words' },
  { id: 'cvc', label: 'CVC Words', short: 'CVC', collection: 'cvc_assessments', column: CVC_TEST_TYPE, href: '/cvc/assessment', resultsHref: '/cvc/results', flag: null, group: 'CVC Words' },
  { id: 'nwf', label: 'Nonsense Word Fluency', short: 'NWF', collection: 'assessments', column: NWF_TEST_TYPE, href: '/screener/assessment', resultsHref: '/screener/results', flag: 'benchmarkScreener', group: null, defaultMax: 25 },
  { id: 'pa4k', label: 'Phonological Awareness (4K)', short: 'PA 4K', collection: 'assessments', tests: PA4K_TEST_TYPES, href: '/pa4k/assessment', resultsHref: '/pa4k/results', testInUrl: true, flag: 'pa4kAssessments', group: null, defaultMax: 5 },
  { id: 'pa_screener', label: 'Phonemic Awareness', short: 'PA', collection: 'assessments', tests: PA_STEP_TYPES, href: '/pa/screener', resultsHref: '/pa/results', testInUrl: true, flag: 'paScreener', group: null, defaultMax: 5 },
]);

/** Every row of the table, offered or not (tests walk this). */
export const allDashboardAssessments = () => ASSESSMENTS.map((a) => ({ ...a }));

/** The assessments the dropdown offers for this teacher, in order. */
export function dashboardAssessments(profile) {
  return ASSESSMENTS.filter((a) => {
    if (a.flag && !isFeatureEnabled(a.flag)) return false;
    if (a.group && !isGroupVisible(profile, a.group)) return false;
    return true;
  }).map((a) => ({ ...a }));
}

/** "View Full Results" for an assessment: its Results page. */
export const resultsHrefFor = (assessment) => (assessment && assessment.resultsHref) || '/results';

/** The offered assessment with this id, else Letters & Sounds. */
export function resolveDashboardAssessment(id, offered) {
  return offered.find((a) => a.id === id) || offered.find((a) => a.id === LETTER_SOUND_ID);
}

/** The result collections the Dashboard reads beyond `assessments` for these assessments. */
export function extraCollectionsFor(offered) {
  return offered.map((a) => a.collection).filter((c) => c !== 'assessments');
}

/**
 * The Test Type options for an assessment: [{ key, label }]. Letters & Sounds:
 * the shared row table; PA: its tests; the rest: [] (the dropdown is greyed out).
 */
export function testOptionsFor(assessment) {
  if (!assessment) return [];
  if (assessment.id === LETTER_SOUND_ID) return letterSoundRows().map((row) => ({ key: row.key, label: row.label }));
  if (assessment.tests) return assessment.tests.map((t) => ({ key: t, label: TEST_TYPE_LABELS[t] || t }));
  return [];
}

/** A PA test key the assessment lists, else its first test. */
export function resolvePaTest(assessment, key) {
  if (!assessment || !assessment.tests) return null;
  return assessment.tests.includes(key) ? key : assessment.tests[0];
}

/**
 * The result column the view shows. Letters & Sounds: the row's DOC test type
 * ('sounds' for every Sounds row); PA: the picked test; else the fixed column.
 */
export function columnFor(assessment, letterKey, paTest) {
  if (!assessment || assessment.id === LETTER_SOUND_ID) return runSpecForKey(letterKey).testType;
  if (assessment.tests) return resolvePaTest(assessment, paTest);
  return assessment.column;
}

/** The heading the legend and panels use ("Sounds", "High Frequency Words", "Rhyme Recognition"). */
export function headingFor(assessment, letterKey, paTest) {
  if (!assessment || assessment.id === LETTER_SOUND_ID) return headingForKey(letterKey);
  if (assessment.tests) return TEST_TYPE_LABELS[resolvePaTest(assessment, paTest)] || assessment.label;
  return assessment.label;
}

/** The name of the picked test for the hover text ("Uppercase", "Rhyme Recognition"), or '' for an assessment without a Test list. */
function testLabelFor(assessment, letterKey, paTest) {
  if (!assessment) return '';
  if (assessment.id === LETTER_SOUND_ID) {
    const row = letterSoundRows().find((r) => r.key === letterKey);
    return row ? row.label : '';
  }
  if (assessment.tests) return TEST_TYPE_LABELS[resolvePaTest(assessment, paTest)] || '';
  return '';
}

/** Where Take goes. Letters & Sounds: the v3.84.10 mapping; the two PA assessments: their page with the test in the URL. */
export function takeHrefFor(assessment, letterKey, paTest) {
  if (!assessment || assessment.id === LETTER_SOUND_ID) return takeAssessmentHref(letterKey);
  if (assessment.tests && assessment.testInUrl) return `${assessment.href}?testType=${encodeURIComponent(resolvePaTest(assessment, paTest))}`;
  return assessment.href;
}

/** The button text: "Take L&S", "Take HFW"… — never wider than today's "Take Assessment". */
export function takeLabelFor(assessment) {
  return `Take ${assessment ? assessment.short : 'L&S'}`;
}

/**
 * The full name, for the hover text: "Take Letters & Sounds – Uppercase with
 * Ada Lovelace". The screen-reader label starts with the visible text (WCAG
 * 2.5.3): "Take L&S: Letters & Sounds – Uppercase, Ada Lovelace".
 */
export function takeTitleFor(assessment, letterKey, paTest, studentName) {
  const test = testLabelFor(assessment, letterKey, paTest);
  const name = assessment ? assessment.label : 'Letters & Sounds';
  return `Take ${name}${test ? ` – ${test}` : ''}${studentName ? ` with ${studentName}` : ''}`;
}
export function takeAriaLabelFor(assessment, letterKey, paTest, studentName) {
  const test = testLabelFor(assessment, letterKey, paTest);
  const name = assessment ? assessment.label : 'Letters & Sounds';
  return `${takeLabelFor(assessment)}: ${name}${test ? ` – ${test}` : ''}${studentName ? `, ${studentName}` : ''}`;
}

const isPercentColumn = (column) => column === HFW_TEST_TYPE || column === CVC_TEST_TYPE;
const PERCENT_LEGEND = Object.freeze({ mastery: '80-100%', emerging: '50-79%', needs_support: '0-49%' });

/**
 * The legend for a column, best band first: [{ key, label, range }]. HFW / CVC
 * read in percent (each test has its own word count); the rest in points on
 * the family's legend scale (letter tests: 26).
 */
export function legendFor(column) {
  if (isPercentColumn(column)) {
    return bandsForTestType(column, 100).map((b) => ({ key: b.key, label: b.label, range: PERCENT_LEGEND[b.key] }));
  }
  const max = letterSoundDocTestTypes().includes(column) ? LETTER_MAX : undefined;
  return bandsForTestType(column, max).map((b) => ({ key: b.key, label: b.label, range: bandRangeText(b) }));
}

/** The one-line note under an HFW / CVC legend, else null. */
export function percentLegendNote(column) {
  return isPercentColumn(column) ? 'Share of words read correctly on the student\'s latest test.' : null;
}

// ── Latest result per student ───────────────────────────────────────────────

const QUARTER_WEIGHT = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
const quarterWeightOf = (quarter) => (quarter ? QUARTER_WEIGHT[String(quarter).substring(4)] || 0 : 0);
const millisOf = (ts) => {
  if (!ts) return NaN;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  return new Date(ts).getTime();
};
const scoreOf = (data) => (data.correctCount !== undefined
  ? data.correctCount
  : Object.values(data.responses || {}).filter((v) => v === 'Correct').length);

const LETTER_DEFAULT_MAX = 26;
const defaultMaxFor = (column) => {
  const a = ASSESSMENTS.find((x) => x.column === column || (x.tests && x.tests.includes(column)));
  return a && a.defaultMax ? a.defaultMax : null;
};

/**
 * The item total a score is out of. Letter/Sound doc tests keep the Dashboard's
 * rule since v3.85.0 (totalLetters, else 26); HFW / CVC their totalWords; NWF
 * and PA their totalLetters, else the family's item count.
 */
export function totalItemsOf(column, data) {
  if (letterSoundDocTestTypes().includes(column)) return data.totalLetters || LETTER_DEFAULT_MAX;
  const total = data.totalPossible ?? data.totalLetters ?? data.totalWords ?? (data.responses ? Object.keys(data.responses).length : null);
  return total || defaultMaxFor(column);
}

/**
 * Latest result per (column, student) — the Dashboard's rule, unchanged since
 * v3.2 and now shared by every column: an N/A doc (correctCount -1) is not a
 * result; the higher quarter wins, then the later timestamp.
 *
 * @param {Array<{ column: string, data: object }>} entries
 * @returns {Object<string, Object<string, { score, totalItems, quarter, timestamp }>>}
 */
export function latestByColumn(entries) {
  const out = {};
  (entries || []).forEach(({ column, data }) => {
    if (!column || !data || !data.studentId) return;
    const score = scoreOf(data);
    if (score === -1) return;
    // A PA screener step saved as "Not administered" has no band and is not a
    // result (PA Results shows it unbanded) — it never hides an earlier score.
    if (data.administration === ADMIN_NOT_ADMINISTERED) return;
    if (!out[column]) out[column] = {};
    const existing = out[column][data.studentId];
    const currentQ = quarterWeightOf(data.quarter);
    const existingQ = existing ? quarterWeightOf(existing.quarter) : 0;
    const currentMillis = millisOf(data.timestamp);
    const existingMillis = existing ? millisOf(existing.timestamp) : 0;
    if (!existing || currentQ > existingQ || (currentQ === existingQ && currentMillis > existingMillis)) {
      out[column][data.studentId] = { score, totalItems: totalItemsOf(column, data), quarter: data.quarter, timestamp: data.timestamp };
    }
  });
  return out;
}

/** The columns an `assessments` doc can feed: the Letter/Sound doc tests, NWF and the PA tests. */
export function assessmentsColumns() {
  return [...new Set([...letterSoundDocTestTypes(), NWF_TEST_TYPE, ...PA4K_TEST_TYPES, ...PA_STEP_TYPES])];
}

// ── Tab memory (sessionStorage, best-effort — like the Test Type pick) ──────

const ASSESSMENT_KEY = 'dashboard.assessment';
const paTestKey = (id) => `dashboard.paTest.${id}`;
const recall = (key) => {
  try { return sessionStorage.getItem(key); } catch (e) { return null; }
};
const remember = (key, value) => {
  try { sessionStorage.setItem(key, value); } catch (e) { /* the URL still carries the pick */ }
};
export const recallDashboardAssessment = () => recall(ASSESSMENT_KEY);
export const rememberDashboardAssessment = (id) => remember(ASSESSMENT_KEY, id);
export const recallDashboardPaTest = (id) => recall(paTestKey(id));
export const rememberDashboardPaTest = (id, test) => remember(paTestKey(id), test);
